import { useEffect, useState, useCallback } from 'react';

type useFetchApiArgs = {
  endpoint: string;
  fetchConfig?: RequestInit;
  initFetch?: boolean;
};

type fetchState<T> = {
  isLoading: boolean;
  status?: number;
  payload: T | undefined;
  hasError: boolean;
};

export type UseFetchApiOutput<T> = fetchState<T> & {
  triggerFetch: () => void;
};

export const useFetchApi = <T>({
  endpoint,
  fetchConfig,
  initFetch = true,
}: useFetchApiArgs): UseFetchApiOutput<T> => {
  const [state, setState] = useState<fetchState<T>>({
    isLoading: initFetch,
    hasError: false,
    payload: undefined,
  });

  const fetchData = useCallback(async () => {
    setState((state) => ({ ...state, isLoading: true, hasError: false }));

    const response = await fetch(endpoint, {
      credentials: 'same-origin',
      ...fetchConfig,
    });

    let payload;
    try {
      payload = await response.json();
    } catch (error) {
      if (!response.ok) {
        payload = error;
      }
    }
    setState({
      payload,
      hasError: !response.ok,
      status: response.status,
      isLoading: false,
    });
  }, [endpoint, fetchConfig]);

  useEffect(() => {
    if (initFetch) {
      void fetchData();
    }
  }, [initFetch, fetchData]);

  return {
    ...state,
    triggerFetch: fetchData,
  };
};
